import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  Box,
  IconButton,
  Stack,
  styled,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import LockClockOutlinedIcon from '@mui/icons-material/LockClockOutlined';
import Text from 'components/mui/Text';
import AppButtonComponent from 'components/mui/AppButtonComponent';
import { ACCOUNT_SETUP_STEP_KEY } from 'utils/investmentEligibility.utils';

/** Verb for the action the customer was blocked on. */
const ACTION_LABELS = {
  invest: 'start investing',
  redeem: 'redeem',
  switch: 'switch funds',
  stp: 'start an STP',
  swp: 'start an SWP',
};

const PENDING_COLOR = '#F5A623';
const DONE_COLOR = '#4CAF50';
const ACCENT_COLOR = '#46A8DC';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    background: theme.palette.custom?.background?.popup || '#13172A',
    borderRadius: '16px',
    color: '#fff',
    maxWidth: 420,
    width: '100%',
    margin: theme.spacing(2),
    position: 'relative',
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.65)',
    backdropFilter: 'blur(8px)',
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(4, 3, 3, 3),
}));

/**
 * One row of the checklist. Completed steps are ticked off, the step the
 * customer owes is highlighted, and anything after it is dimmed so the list
 * reads as progress rather than a wall of demands.
 */
const StepRow = ({ step }) => {
  const { label, completed, isPending } = step;

  let icon = (
    <RadioButtonUncheckedRoundedIcon
      sx={{ fontSize: 18, color: 'rgba(255,255,255,0.25)' }}
    />
  );

  if (completed) {
    icon = <CheckCircleRoundedIcon sx={{ fontSize: 18, color: DONE_COLOR }} />;
  } else if (isPending) {
    icon = <LockClockOutlinedIcon sx={{ fontSize: 18, color: PENDING_COLOR }} />;
  }

  let labelColor = 'rgba(255,255,255,0.45)';
  if (completed) {
    labelColor = 'rgba(255,255,255,0.55)';
  } else if (isPending) {
    labelColor = '#fff';
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: '10px',
        border: '1px solid',
        borderColor: isPending ? alpha(PENDING_COLOR, 0.45) : 'transparent',
        backgroundColor: isPending
          ? alpha(PENDING_COLOR, 0.1)
          : 'rgba(255,255,255,0.03)',
      }}
    >
      {icon}
      <Text
        sx={{
          flex: 1,
          fontSize: '13px',
          fontWeight: isPending ? 600 : 400,
          color: labelColor,
          textAlign: 'left',
        }}
      >
        {label}
      </Text>
      {isPending && (
        <Text
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: PENDING_COLOR,
            whiteSpace: 'nowrap',
          }}
        >
          Pending
        </Text>
      )}
    </Stack>
  );
};

StepRow.propTypes = {
  step: PropTypes.shape({
    label: PropTypes.string,
    completed: PropTypes.bool,
    isPending: PropTypes.bool,
  }).isRequired,
};

/**
 * Raised when a customer taps Invest (or redeem / switch / STP / SWP) while an
 * onboarding step is still pending. The customer is never dropped onto the
 * onboarding screen without being asked - Complete takes them to the step they
 * owe, Cancel leaves them exactly where they were.
 */
const OnboardingPendingModal = ({
  open,
  pendingStep,
  steps,
  actionType = 'invest',
  onClose,
  onComplete,
}) => {
  const stepLabel = pendingStep?.nounLabel || pendingStep?.label;
  const actionLabel = ACTION_LABELS[actionType] || 'continue';

  // The gate can land on a step that is not part of the listed sequence (a KYC
  // sent back for review, for instance). Show it as the pending row so the list
  // never claims everything is done while the customer is still blocked.
  const checklist = React.useMemo(() => {
    const rows = Array.isArray(steps) ? steps : [];
    if (rows.length === 0) return rows;
    if (rows.some((step) => step?.isPending === true)) return rows;
    if (!pendingStep?.label) return rows;

    // Only a step the list does not already carry is worth adding. The generic
    // "Account Setup" fallback stands for this whole checklist, and a listed
    // step is not a second thing to do — appending either one grew the list a
    // phantom extra row on top of the four real ones.
    if (pendingStep.key === ACCOUNT_SETUP_STEP_KEY) return rows;

    const listed = rows.findIndex(
      (step) =>
        (pendingStep.key && step?.key === pendingStep.key) ||
        step?.label === pendingStep.label,
    );

    if (listed !== -1) {
      return rows.map((step, index) =>
        index === listed
          ? { ...step, completed: false, isPending: true }
          : step,
      );
    }

    return [
      ...rows,
      {
        key: pendingStep.key || 'pending',
        label: pendingStep.label,
        completed: false,
        isPending: true,
      },
    ];
  }, [steps, pendingStep]);

  const doneCount = checklist.filter((step) => step?.completed === true).length;

  const description = stepLabel
    ? `Finish ${stepLabel} to ${actionLabel}. Everything you have completed so far is saved.`
    : `Finish your account setup to ${actionLabel}. Everything you have completed so far is saved.`;

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="xs">
      <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'rgba(255,255,255,0.5)',
          '&:hover': { color: '#fff' },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <StyledDialogContent>
        <Stack alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: alpha(PENDING_COLOR, 0.14),
            }}
          >
            <LockClockOutlinedIcon sx={{ fontSize: 26, color: PENDING_COLOR }} />
          </Box>

          <Text sx={{ fontSize: '18px', fontWeight: 700, color: '#fff' }}>
            Onboarding Pending
          </Text>

          <Text
            sx={{
              fontSize: '13px',
              lineHeight: 1.5,
              color: 'rgba(255,255,255,0.6)',
              textAlign: 'center',
            }}
          >
            {description}
          </Text>
        </Stack>

        {checklist.length > 0 && (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Text
                sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.4)',
                }}
              >
                Account Setup
              </Text>
              <Text sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                {doneCount} of {checklist.length} done
              </Text>
            </Stack>

            <Stack spacing={0.75} sx={{ mb: 3 }}>
              {checklist.map((step) => (
                <StepRow key={step?.key || step?.label} step={step} />
              ))}
            </Stack>
          </>
        )}

        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.25}>
          <AppButtonComponent
            variant="outlined"
            color="primary"
            fullWidth
            onClick={onClose}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '14px',
              py: 1.1,
              color: 'rgba(255,255,255,0.85)',
              borderColor: 'rgba(255,255,255,0.25)',
              '&:hover': {
                borderColor: 'rgba(255,255,255,0.45)',
                backgroundColor: 'rgba(255,255,255,0.06)',
              },
            }}
          >
            Cancel
          </AppButtonComponent>

          <AppButtonComponent
            variant="contained"
            color="primary"
            fullWidth
            onClick={onComplete}
            endIcon={<ArrowForwardRoundedIcon />}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '14px',
              py: 1.1,
              boxShadow: 'none',
              '&:hover': { boxShadow: `0 0 0 3px ${alpha(ACCENT_COLOR, 0.2)}` },
            }}
          >
            Complete
          </AppButtonComponent>
        </Stack>
      </StyledDialogContent>
    </StyledDialog>
  );
};

OnboardingPendingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  pendingStep: PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.string,
    nounLabel: PropTypes.string,
    path: PropTypes.string,
  }),
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      completed: PropTypes.bool,
      isPending: PropTypes.bool,
    }),
  ),
  actionType: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default OnboardingPendingModal;
